import { ForwardedRef, forwardRef } from 'react';
import styled from 'styled-components';
import { theme } from './divider.theme';

type Orientation = 'horizontal' | 'vertical';

export interface DividerProps {
	/**
	 * Defaults to 16px.
	 * @default 16px
	 */
	offset?: string;

	/**
	 * Either `vertical` or `horizontal`. Defaults to `horizontal`.
	 *
	 * @default 'horizontal'
	 */
	orientation?: Orientation;

	/**
	 * Whether or not the component is purely decorative. When true, accessibility-related attributes
	 * are updated so that that the rendered element is removed from the accessibility tree.
	 *
	 * @default true
	 */
	decorative?: boolean;
}

const elements = {
	container: styled.div<{ $offset?: string }>`
		background-color: ${theme.getValue('baseBackground')};

		&[data-orientation='horizontal'] {
			margin: 16px 0;
			height: 1px;
			width: 100%;
			margin: ${({ $offset }) => $offset ?? 0} 0;
		}
		&[data-orientation='vertical'] {
			margin: 0 16px;
			height: 100%;
			width: 1px;
			margin: 0 ${({ $offset }) => $offset ?? 0};
		}
	`,
};

function Divider(props: DividerProps, ref: ForwardedRef<HTMLDivElement>): JSX.Element {
	const isDecorative = props.decorative ?? true;
	const semanticProps = isDecorative
		? {
				role: 'none',
			}
		: {
				// `aria-orientation` defaults to `horizontal` so we only need it if `orientation` is vertical
				'aria-orientation': props.orientation === 'vertical' ? props.orientation : undefined,
				role: 'separator',
			};

	return (
		<elements.container $offset={props.offset} data-orientation={props.orientation ?? 'horizontal'} ref={ref} {...semanticProps} />
	);
}

const _Divider = forwardRef(Divider);

export { _Divider as Divider };

Divider.displayName = 'CellulaDivider';
